import { processChatInput, resolveConversationHistory, showHangoutFreeAccountModal } from './message-utils.js';
import { HangoutState } from '../state/HangoutState.js';
import { GROK_WORKER_URL } from '../../../js/config.js';
import { characterManager } from './character-manager.js';
import { initHangoutFromUrl } from './session-manager.js';
import { getUserModelOverride, getUserVibe } from './constants.js';
import { enrichCharactersWithOutfits } from './request-builder.js';
import { fetchCache } from '../../utils/fetchCache.js';

export const handleHelpInspireButtonClick = async () => {
    const helpInspireButton = document.getElementById('help-inspire-button');
    const chatInput = /** @type {HTMLInputElement} */ (document.getElementById('chat-input'));
    const inputWrap = chatInput?.closest('.chat-input-field-wrap');
    const inspireLoading = /** @type {HTMLElement | null} */ (document.getElementById('chat-input-inspire-loading'));

    if (!helpInspireButton || !chatInput) return;

    // Check authentication first
    const authToken = await window.tokenManager?.getValidAccessToken?.() ?? null;
    if (!authToken) {
        await showHangoutFreeAccountModal('inspiration-unauth');
        return;
    }

    // Add loading state
    helpInspireButton.disabled = true;
    if (inputWrap) {
        inputWrap.classList.add('chat-input-field-wrap--inspire-loading');
    }
    if (inspireLoading) {
        inspireLoading.hidden = false;
    }
    chatInput.setAttribute('aria-busy', 'true');

    try {
        const hangoutState = HangoutState.getInstance();
        const conversationId = hangoutState.getConversationId();
        const messages = await resolveConversationHistory({
            hangoutState
        });
        const session = hangoutState.getCurrentSession();
        const scenario = hangoutState.getSelectedScenario();
        const characters = hangoutState.getCharacters();
        const promptId = await getUserVibe();
        const modelOverride = getUserModelOverride();
        const { storage } = await import('../../utils/storage.js');
        const currentLang = storage.getLanguage();
        const customScenarioDescriptionRaw = storage.get('hangout:customScenarioDescription');
        const customScenarioDescription = typeof customScenarioDescriptionRaw === 'string' && customScenarioDescriptionRaw.trim() !== ''
            ? customScenarioDescriptionRaw
            : null;
        const inputDraft = chatInput.value.trim();

        const enrichedCharacters = characters.map(c => ({ ...c }));
        await enrichCharactersWithOutfits(enrichedCharacters);

        let locationName = null;
        const spot = await hangoutState.getSelectedSpot();
        locationName = spot?.locationName || spot?.name || null;

        // Prepare payload
        const payload = {
            ...(conversationId ? { conversationId } : {}),
            messages: messages.slice(-10).map(m => ({
                role: m.role,
                content: m.content,
                ...(m.role !== 'user' && m.characterName ? { characterName: m.characterName } : {})
            })),
            characters: enrichedCharacters.map(char => ({
                id: char.id,
                name: char.name,
                ...(char.goal?.trim?.() ? { goal: char.goal.trim() } : {}),
                outfitName: char.outfitName || 'default outfit'
            })),
            scenario: {
                id: scenario?.id ?? session?.scenarioId ?? null,
                name: scenario?.name || null,
                description: customScenarioDescription || scenario?.description || 'A conversation'
            },
            currentTime: session?.timeOfDay ?? null,
            language: currentLang,
            promptId,
            ...(modelOverride ? { modelOverride } : {}),
            ...(locationName ? { location: locationName } : {}),
            ...(inputDraft ? { inputDraft } : {})
        };

        // Use the GROK_WORKER_URL for the new endpoint
        // Note: using /v1/ prefix to ensure worker parses category correctly
        const data = await fetchCache.fetchJSON(`${GROK_WORKER_URL}/v1/hangout/inspiration`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(payload)
        });

        if (data.suggestion) {
            // Set input value and trigger events to update UI
            chatInput.value = data.suggestion;
            chatInput.dispatchEvent(new Event('input', { bubbles: true }));
            chatInput.focus();

            // Trigger button toggle to show send button
            const { toggleSendMicButton } = await import('./chat-input.js');
            if (toggleSendMicButton) toggleSendMicButton();
        }

    } catch (error) {
        console.error('Error getting inspiration:', error);

        if (error?.status === 401 || error?.message === 'Unauthorized') {
            await showHangoutFreeAccountModal('inspiration-unauthorized');
            return;
        }

        import('../../utils/UnifiedNotificationSystem.js').then(({ showError }) => {
            showError('Could not get inspiration at the moment.');
        });
    } finally {
        // Restore button state
        helpInspireButton.disabled = false;
        if (inputWrap) {
            inputWrap.classList.remove('chat-input-field-wrap--inspire-loading');
        }
        if (inspireLoading) {
            inspireLoading.hidden = true;
        }
        chatInput.removeAttribute('aria-busy');
    }
};

export const handleFloatingButtonClick = async () => {
    const isMobile = window.innerWidth <= 1024;

    if (isMobile) {
        await handleMicSendButtonClick();
        return;
    }

    await handleVoiceRecording();
};

export const handleMicSendButtonClick = async () => {
    /** @type {HTMLInputElement} */
    const messageInput = /** @type {HTMLInputElement} */ (document.getElementById('chat-input'));
    if (!messageInput) return;

    const hasText = messageInput.value.trim().length > 0;

    if (hasText) {
        // Send message
        processChatInput(messageInput.value.trim());
        messageInput.value = '';
    } else {
        // Start voice recording
        await handleVoiceRecording();
    }
};

export const handleVoiceRecording = async () => {
    const { voiceRecordingService } = await import('../../services/voiceRecordingService.js');
    const { showInfo, showError, showSuccess } = await import('../../utils/UnifiedNotificationSystem.js');

        // Check if recording is supported
        if (!voiceRecordingService.isSupported()) {
            showError('Voice recording is not supported in your browser', { duration: 3000 });
            return;
        }

        const sendButton = document.getElementById('mic-send-button');
        const sendButtonFloating = document.getElementById('mic-send-button-floating');
        const chatInput = document.getElementById('chat-input');

        if (voiceRecordingService.getRecordingState().isRecording) {
            // Stop recording
            showInfo('Processing voice...', { duration: 2000 });

            try {
                const audioBlob = await voiceRecordingService.stopRecording();

                // Remove recording animation class from both buttons (stops pulse)
                if (sendButton) {
                    sendButton.classList.remove('recording');
                }
                if (sendButtonFloating) {
                    sendButtonFloating.classList.remove('recording');
                }

                // Reset placeholder
                if (chatInput) {
                    const { updateMessagePlaceholder } = await import('../ui/chat-input.js');
                    await updateMessagePlaceholder();
                }

                // Transcribe audio
                const transcribedText = await voiceRecordingService.transcribeAudio(audioBlob);

                if (transcribedText && chatInput) {
                    chatInput.value = transcribedText;
                    chatInput.dispatchEvent(new Event('input', { bubbles: true }));
                    showSuccess('Voice transcribed successfully!', { duration: 2000 });
                } else {
                    showError('No speech detected. Please try again.', { duration: 3000 });
                }
            } catch (error) {
                console.error('Voice recording error:', error);
                showError('Failed to process voice: ' + error.message, { duration: 3000 });

                // Remove recording animation class from both buttons (stops pulse)
                if (sendButton) {
                    sendButton.classList.remove('recording');
                }
                if (sendButtonFloating) {
                    sendButtonFloating.classList.remove('recording');
                }

                // Reset placeholder
                if (chatInput) {
                    const { updateMessagePlaceholder } = await import('../ui/chat-input.js');
                    await updateMessagePlaceholder();
                }
            }
        } else {
            // Start recording
            try {
                await voiceRecordingService.startRecording();

                // Add recording animation class to both buttons (triggers pulse animation)
                // Icon stays the same, only button pulses
                if (sendButton) {
                    sendButton.classList.add('recording');
                }
                if (sendButtonFloating) {
                    sendButtonFloating.classList.add('recording');
                }

                // Update placeholder
                if (chatInput) {
                    chatInput.setAttribute('placeholder', 'Recording...');
                }

                showInfo('Recording... Click again to stop', { duration: 2000 });
            } catch (error) {
                console.error('Failed to start recording:', error);
                if (error.name === 'NotAllowedError') {
                    showError('Microphone access denied. Please allow microphone access in your browser settings.', { duration: 5000 });
                } else {
                    showError('Failed to start recording: ' + error.message, { duration: 3000 });
                }
            }
        }
};

export const handleChatInputKeypress = async (e) => {
    if (e.key !== 'Enter') return;

    const chatInput = e.target;
    processChatInput(chatInput.value.trim());
    chatInput.value = '';
    await characterManager.updateMentionHighlights();
};

export const handleSettingsButtonClick = async () => {
    const hangoutState = HangoutState.getInstance();

    if (!hangoutState.getSelectedScenario()) {
        console.warn('Settings modal cannot be opened: No scenario selected. Initializing session...');
        await initHangoutFromUrl(); // Ensure session is initialized

        if (!hangoutState.getSelectedScenario()) {
            console.error('Settings modal cannot be opened: Still no scenario selected after initialization.');
            import('../../utils/UnifiedNotificationSystem.js').then(({ showWarning }) => {
                showWarning('Unable to open settings: Session not fully initialized. Please try again.', { icon: 'cog', duration: 5000 });
            });
            return;
        }
    }

    const { getSettingsModalInstance, openSettingsModal } = await import('./modals/settings-modal.js');
    await getSettingsModalInstance();
    await openSettingsModal();
};
